import { useEffect, useState } from 'react'
import { useImmer } from 'use-immer'
import {
  Box, Button, Card, CardBody, CardHeader, Checkbox, Divider, Flex, FormControl,
  FormHelperText, FormLabel, Grid, GridItem, Heading, Input, Text, Textarea
} from '@chakra-ui/react'
import { UiBreadcrumb } from 'components/common'
import { useAppContext } from 'hooks/useAppContext'
import { getConfigByName, updateConfigByName, createConfig } from 'api'
import { APP_CONFIG_FIELDS } from 'lib'

const BreadcrumbNav = [
  {
    label: 'Survey Email Automations',
    isCurrentPage: true
  }
]

// Order + copy for each milestone automation shown in the UI. The `key` must
// match the milestone keys used by the backend surveyEmailService.
const MILESTONE_META = [
  {
    key: 'preProductionCall',
    label: 'Post Pre-Production Call',
    trigger: 'When the "Complete Pre-Production Call & E-mail Client" task is marked complete'
  },
  {
    key: 'productionPhase',
    label: 'Moved to Production',
    trigger: 'When a project moves from the Pre-Production phase to the Production phase'
  },
  {
    key: 'preInstallConference',
    label: 'Pre-Installation Conference',
    trigger: 'When the "Conduct Pre-Installation Conference" task is marked complete'
  },
  {
    key: 'invoicing',
    label: 'Installed / Moved to Invoicing',
    trigger: 'When the "Move Project to Invoicing" task is marked complete'
  }
]

// Fallback content mirrored from the backend DEFAULT_CONFIG. Used only when the
// `surveyEmails` config row does not yet exist.
const DEFAULT_CONFIG = {
  enabled: true,
  surveyLink: '',
  linkText: 'CLICK HERE TO COMPLETE SURVEY',
  ccAddress: '',
  fromAddress: '"Jordan Robson" <jrobson@goldenrulesigns.com>',
  greeting: 'Hi {customerName},',
  signoff: ['Best,', 'The Golden Rule Signs Team'],
  milestones: {
    preProductionCall: {
      enabled: true,
      subject: 'Thank You! Tell Us About Your Experience So Far',
      surveyLink: 'https://survey.zohopublic.com/zs/Q3CKvQ',
      paragraphs: [
        'Thank you for moving forward with your sign project! As we transition into the next phase, we\u2019d love to hear about your experience so far. Your feedback helps us improve and ensures we\u2019re meeting your expectations.',
        'This quick survey takes less than 2 minutes: {survey}',
        'If you have any immediate questions or concerns, feel free to reply to this email. We appreciate your time and look forward to bringing your project to life!'
      ]
    },
    productionPhase: {
      enabled: true,
      subject: 'Your Feedback Helps! Quick Check-In on Your Project',
      surveyLink: 'https://survey.zohopublic.com/zs/ltCKeA',
      paragraphs: [
        'Your project is moving forward, and we\u2019re excited to transition into production! Before we do, we\u2019d love to hear how things have gone so far.',
        'Could you take a minute to share your thoughts? Your feedback helps us continue to improve: {survey}',
        'As always, let us know if there\u2019s anything we can do to assist you. Thanks for being part of the process!'
      ]
    },
    preInstallConference: {
      enabled: true,
      subject: 'Preparing for Installation \u2013 We\u2019d Love Your Input',
      surveyLink: 'https://survey.zohopublic.com/zs/xZCKCR',
      paragraphs: [
        'Your sign is almost ready for installation! Before we move forward, we\u2019d love to get your thoughts on your experience so far.',
        'This short survey helps us ensure everything is on track: {survey}',
        'Please reach out if you have any questions or if there\u2019s anything we can do to make the next steps even smoother. We appreciate your time!'
      ]
    },
    invoicing: {
      enabled: true,
      subject: 'Your Sign is Installed! Tell Us What You Think',
      surveyLink: 'https://survey.zohopublic.com/zs/fkCK26',
      paragraphs: [
        'We hope you\u2019re loving your new sign! Now that installation is complete, we\u2019d love to hear about your experience.',
        'This quick survey helps us continue to improve: {survey}',
        'Your feedback is invaluable, and if there\u2019s anything we can do to assist you, just let us know. Thank you for trusting us with your project!'
      ]
    }
  }
}

// Paragraphs are stored as an array in config but edited as a single textarea
// where paragraphs are separated by a blank line.
const paragraphsToText = (paragraphs = []) => (paragraphs || []).join('\n\n')
const textToParagraphs = (text = '') => text.split(/\n\s*\n/).map(p => p.trim()).filter(Boolean)

const signoffToText = (signoff) => (Array.isArray(signoff) ? signoff.join('\n') : (signoff || ''))
const textToSignoff = (text = '') => text.split('\n')

const toFormState = (config) => ({
  enabled: config.enabled !== false,
  surveyLink: config.surveyLink || '',
  linkText: config.linkText || '',
  ccAddress: config.ccAddress || '',
  fromAddress: config.fromAddress || '',
  greeting: config.greeting || '',
  signoff: signoffToText(config.signoff),
  milestones: MILESTONE_META.reduce((acc, { key }) => {
    const m = (config.milestones || {})[key] || {}
    acc[key] = {
      enabled: m.enabled !== false,
      subject: m.subject || '',
      surveyLink: m.surveyLink || '',
      paragraphs: paragraphsToText(m.paragraphs)
    }
    return acc
  }, {})
})

const toConfigPayload = (form) => ({
  enabled: form.enabled,
  surveyLink: form.surveyLink,
  linkText: form.linkText,
  ccAddress: form.ccAddress,
  fromAddress: form.fromAddress,
  greeting: form.greeting,
  signoff: textToSignoff(form.signoff),
  milestones: MILESTONE_META.reduce((acc, { key }) => {
    const m = form.milestones[key]
    acc[key] = {
      enabled: m.enabled,
      subject: m.subject,
      ...(m.surveyLink ? { surveyLink: m.surveyLink } : {}),
      paragraphs: textToParagraphs(m.paragraphs)
    }
    return acc
  }, {})
})

const mergeLoadedConfig = (value) => {
  const milestones = MILESTONE_META.reduce((acc, { key }) => {
    acc[key] = {
      ...(DEFAULT_CONFIG.milestones[key] || {}),
      ...((value?.milestones || {})[key] || {})
    }
    return acc
  }, {})
  return {
    ...DEFAULT_CONFIG,
    ...value,
    milestones
  }
}

export const SurveyEmailsConfig = () => {
  const { createToast } = useAppContext()
  const [form, setForm] = useImmer(toFormState(DEFAULT_CONFIG))
  const [exists, setExists] = useState(false)
  const [isSaving, setIsSaving] = useState(false)

  const load = async () => {
    try {
      const res = await getConfigByName(APP_CONFIG_FIELDS.SurveyEmails)
      const row = res.data || res
      const value = typeof row.value === 'string' ? JSON.parse(row.value) : row.value
      setForm(() => toFormState(mergeLoadedConfig(value)))
      setExists(true)
    } catch (err) {
      // 404 => row not seeded yet; keep defaults and create on first save.
      if (err.response?.status === 404) {
        setExists(false)
      } else {
        createToast('error', 'Error', err.response?.data ?? err.message)
      }
    }
  }

  useEffect(() => {
    load()
  }, [])

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsSaving(true)
    try {
      const value = JSON.stringify(toConfigPayload(form))
      if (exists) {
        await updateConfigByName(APP_CONFIG_FIELDS.SurveyEmails, { value })
      } else {
        await createConfig({ name: APP_CONFIG_FIELDS.SurveyEmails, value })
        setExists(true)
      }
      createToast('success', 'Successfully updated.')
    } catch (err) {
      createToast('error', 'Error', err.response?.data ?? err.message)
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <>
      <UiBreadcrumb items={BreadcrumbNav} />
      <Box as="form" onSubmit={handleSubmit}>
        <Flex justifyContent="space-between" alignItems="center" my={8}>
          <Heading size="lg">Survey Email Automations</Heading>
          <Button type="submit" colorScheme="blue" isLoading={isSaving}>Save</Button>
        </Flex>

        <Card borderRadius={0}>
          <CardHeader bg="gray.500" p={4} display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
            <Heading color="white" size="sm">Global Settings</Heading>
            <Checkbox
              colorScheme="whiteAlpha"
              color="white"
              isChecked={form.enabled}
              onChange={(e) => setForm(draft => { draft.enabled = e.target.checked })}
            >
              Automations enabled
            </Checkbox>
          </CardHeader>
          <CardBody p={8}>
            <Grid templateColumns="repeat(2, 1fr)" gap={4}>
              <GridItem>
                <FormControl>
                  <FormLabel>Survey link (default)</FormLabel>
                  <Input value={form.surveyLink} onChange={(e) => setForm(draft => { draft.surveyLink = e.target.value })} />
                  <FormHelperText>Used for the {'{survey}'} link unless a milestone overrides it.</FormHelperText>
                </FormControl>
              </GridItem>
              <GridItem>
                <FormControl>
                  <FormLabel>Survey link text</FormLabel>
                  <Input value={form.linkText} onChange={(e) => setForm(draft => { draft.linkText = e.target.value })} />
                </FormControl>
              </GridItem>
              <GridItem>
                <FormControl>
                  <FormLabel>CC address (optional)</FormLabel>
                  <Input value={form.ccAddress} onChange={(e) => setForm(draft => { draft.ccAddress = e.target.value })} />
                </FormControl>
              </GridItem>
              <GridItem>
                <FormControl>
                  <FormLabel>From address</FormLabel>
                  <Input value={form.fromAddress} onChange={(e) => setForm(draft => { draft.fromAddress = e.target.value })} />
                  <FormHelperText>e.g. &quot;Jordan Robson&quot; &lt;jrobson@goldenrulesigns.com&gt;. Must be SES-verified.</FormHelperText>
                </FormControl>
              </GridItem>
              <GridItem>
                <FormControl>
                  <FormLabel>Greeting</FormLabel>
                  <Input value={form.greeting} onChange={(e) => setForm(draft => { draft.greeting = e.target.value })} />
                  <FormHelperText>Supports {'{customerName}'} (first name).</FormHelperText>
                </FormControl>
              </GridItem>
              <GridItem colSpan={2}>
                <FormControl>
                  <FormLabel>Signoff (one line per row)</FormLabel>
                  <Textarea rows={2} value={form.signoff} onChange={(e) => setForm(draft => { draft.signoff = e.target.value })} />
                </FormControl>
              </GridItem>
            </Grid>
          </CardBody>
        </Card>

        {MILESTONE_META.map(({ key, label, trigger }) => (
          <Card key={key} borderRadius={0} mt={8}>
            <CardHeader bg="gray.500" p={4} display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
              <Heading color="white" size="sm">{label}</Heading>
              <Checkbox
                colorScheme="whiteAlpha"
                color="white"
                isChecked={form.milestones[key].enabled}
                onChange={(e) => setForm(draft => { draft.milestones[key].enabled = e.target.checked })}
              >
                Enabled
              </Checkbox>
            </CardHeader>
            <CardBody p={8}>
              <Text fontSize="sm" color="gray.500" mb={4}><b>Trigger:</b> {trigger}</Text>
              <Divider mb={4} />
              <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                <GridItem>
                  <FormControl>
                    <FormLabel>Subject</FormLabel>
                    <Input value={form.milestones[key].subject} onChange={(e) => setForm(draft => { draft.milestones[key].subject = e.target.value })} />
                  </FormControl>
                </GridItem>
                <GridItem>
                  <FormControl>
                    <FormLabel>Survey link</FormLabel>
                    <Input value={form.milestones[key].surveyLink} onChange={(e) => setForm(draft => { draft.milestones[key].surveyLink = e.target.value })} />
                  </FormControl>
                </GridItem>
                <GridItem colSpan={2}>
                  <FormControl>
                    <FormLabel>Body</FormLabel>
                    <Textarea rows={8} value={form.milestones[key].paragraphs} onChange={(e) => setForm(draft => { draft.milestones[key].paragraphs = e.target.value })} />
                    <FormHelperText>Separate paragraphs with a blank line. Use {'{survey}'} for the survey link and {'{customerName}'} for the customer&apos;s first name.</FormHelperText>
                  </FormControl>
                </GridItem>
              </Grid>
            </CardBody>
          </Card>
        ))}

        <Button type="submit" mt={8} colorScheme="blue" isLoading={isSaving}>Save</Button>
      </Box>
    </>
  )
}
